import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../../home/components/Footer';

export default function PoolRepairPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openScheduler = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) widget.click();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="cursor-pointer">
              <div className="text-2xl font-bold leading-tight text-[#2C2C2C]">RW Construction</div>
              <div className="text-sm leading-tight text-gray-600">Las Vegas LLC</div>
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Home</Link>
              <Link to="/services/pool-repair" className="text-sm font-medium text-[#FF6B35] transition-colors cursor-pointer">Pool Repair</Link>
              <Link to="/services/synthetic-grass" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Synthetic Grass</Link>
              <Link to="/services/pavers" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Pavers</Link>
              <button onClick={openScheduler} className="bg-[#FF6B35] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#E55A2B] transition-colors whitespace-nowrap cursor-pointer">
                Get Free Estimate
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/964bba12c2846f126fb533abc83d143e.jpg"
            alt="Professional Pool Repair Services Las Vegas"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-[#FF6B35] px-4 py-2 rounded-full mb-6">
            <i className="ri-water-flash-line text-white"></i>
            <span className="text-white text-sm font-semibold">Expert Pool Services</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Pool Repair &amp; Plaster Services in Las Vegas
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mb-8">
            From minor repairs to complete pool renovations, our licensed technicians deliver exceptional results. We specialize in pool plaster repair, tile replacement, equipment upgrades, and leak detection throughout the Las Vegas Valley.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={openScheduler}
              className="inline-flex items-center gap-2 bg-[#FF6B35] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#E55A2B] transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              Schedule Pool Inspection
            </button>
            <a
              href="tel:7025001790"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call 702-500-1790
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Our Expertise</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Comprehensive Pool Repair Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We handle all aspects of pool repair and maintenance to keep your pool in pristine condition year-round.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-paint-brush-line', title: 'Pool Plaster Repair', desc: 'Expert plaster repair and resurfacing to restore your pool\'s smooth, beautiful finish. We fix cracks, stains, and rough spots.', features: ['Crack repair', 'Stain removal', 'Complete resurfacing', 'Color matching'] },
              { icon: 'ri-drop-line', title: 'Leak Detection & Repair', desc: 'Advanced leak detection technology to find and fix leaks quickly, saving water and preventing structural damage.', features: ['Pressure testing', 'Dye testing', 'Electronic detection', 'Pipe repair'] },
              { icon: 'ri-grid-line', title: 'Tile & Coping Repair', desc: 'Replace cracked or missing tiles and repair damaged coping to restore your pool\'s aesthetic appeal.', features: ['Tile replacement', 'Coping repair', 'Grout restoration', 'Waterline tile'] },
              { icon: 'ri-settings-3-line', title: 'Equipment Repair', desc: 'Repair and replacement of pumps, filters, heaters, and automation systems to keep your pool running efficiently.', features: ['Pump repair', 'Filter service', 'Heater repair', 'Automation'] },
              { icon: 'ri-refresh-line', title: 'Pool Renovation', desc: 'Complete pool makeovers including new plaster, tile, coping, and modern equipment upgrades.', features: ['Full resurface', 'Design updates', 'Energy efficiency', 'Modern features'] },
              { icon: 'ri-shield-check-line', title: 'Structural Repair', desc: 'Fix cracks, settling, and structural issues to ensure your pool remains safe and watertight for years.', features: ['Crack injection', 'Deck leveling', 'Foundation repair', 'Waterproofing'] },
              { icon: 'ri-sun-line', title: 'Solar Pool Heating', desc: 'Harness the Las Vegas sun to heat your pool efficiently and extend your swimming season with eco-friendly solar panels.', features: ['Panel installation', 'System design', 'Energy savings', 'Year-round swimming'] },
            ].map((service, index) => (
              <div key={index} className="bg-[#F8F6F3] rounded-2xl p-8 hover:shadow-xl transition-all group">
                <div className="w-16 h-16 bg-[#FF6B35] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{service.desc}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#FF6B35]"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">How It Works</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Our Pool Repair Process</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We follow a proven process to ensure quality results and complete customer satisfaction.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Free Inspection', desc: 'We thoroughly inspect your pool to identify all issues and provide an accurate assessment.' },
              { step: '02', title: 'Detailed Quote', desc: 'Receive a comprehensive quote with clear pricing and timeline for your approval.' },
              { step: '03', title: 'Expert Repair', desc: 'Our skilled technicians complete the repairs using premium materials and techniques.' },
              { step: '04', title: 'Final Walkthrough', desc: 'We review the completed work with you to ensure your complete satisfaction.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white text-2xl font-bold">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Our Work</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Pool Repair Transformations</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">See the dramatic results we achieve for Las Vegas homeowners.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { img: 'https://readdy.ai/api/search-image?query=Beautiful%20renovated%20swimming%20pool%20with%20new%20blue%20plaster%20finish%20crystal%20clear%20water%20modern%20tile%20work%20Las%20Vegas%20backyard%20sunny%20day%20professional%20photography%20clean%20simple%20background&width=600&height=450&seq=pool-work-001&orientation=landscape', title: 'Complete Pool Resurface', location: 'Henderson, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Luxury%20pool%20with%20new%20waterline%20tile%20installation%20elegant%20mosaic%20pattern%20clear%20blue%20water%20Las%20Vegas%20home%20professional%20pool%20renovation%20photography%20simple%20background&width=600&height=450&seq=pool-work-002&orientation=landscape', title: 'Tile & Coping Replacement', location: 'Summerlin, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Modern%20pool%20equipment%20installation%20new%20pump%20filter%20system%20professional%20pool%20repair%20Las%20Vegas%20residential%20backyard%20clean%20organized%20equipment%20pad%20simple%20background&width=600&height=450&seq=pool-work-003&orientation=landscape', title: 'Equipment Upgrade', location: 'Las Vegas, NV' },
            ].map((project, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="rounded-2xl overflow-hidden mb-4 shadow-lg">
                  <img
                    src={project.img}
                    alt={project.title}
                    className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-lg font-bold text-[#2C2C2C]">{project.title}</h3>
                <p className="text-gray-500 text-sm flex items-center gap-1">
                  <i className="ri-map-pin-line text-[#FF6B35]"></i>
                  {project.location}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#2C2C2C]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Why Choose Us</span>
              <h2 className="text-4xl font-bold text-white mt-3 mb-6">Las Vegas Pool Repair Experts</h2>
              <p className="text-white/80 text-base leading-relaxed mb-8">
                With over 20 years of experience in the Las Vegas Valley, we understand the unique challenges desert pools face. From extreme heat to hard water, we have the expertise to keep your pool in perfect condition.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: 'ri-medal-line', title: '20+ Years', desc: 'Experience' },
                  { icon: 'ri-shield-star-line', title: 'Licensed', desc: '& Insured' },
                  { icon: 'ri-thumb-up-line', title: '500+', desc: 'Pools Repaired' },
                  { icon: 'ri-24-hours-line', title: 'Fast', desc: 'Response Time' },
                ].map((stat, index) => (
                  <div key={index} className="bg-white/10 rounded-xl p-5">
                    <i className={`${stat.icon} text-[#FF6B35] text-3xl mb-2 block`}></i>
                    <div className="text-white font-bold text-xl">{stat.title}</div>
                    <div className="text-white/60 text-sm">{stat.desc}</div>
                  </div>
                ))}
              </div>
              {/* Certifications */}
              <div className="mt-8">
                <h3 className="text-white font-semibold mb-4">Our Certifications</h3>
                <div className="flex flex-wrap gap-3">
                  {[
                    'CPO Certified',
                    'NSPF Certified',
                    'APSP Member',
                    'NPC Certified',
                    'Pool & Hot Tub Alliance'
                  ].map((cert, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                      <i className="ri-award-fill text-[#FF6B35]"></i>
                      <span className="text-white text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20pool%20technician%20inspecting%20swimming%20pool%20equipment%20in%20Las%20Vegas%20residential%20backyard%20wearing%20uniform%20with%20tools%20expert%20pool%20repair%20service%20sunny%20day%20high%20quality%20photography%20simple%20clean%20background&width=600&height=700&seq=pool-tech-001&orientation=portrait"
                alt="Professional Pool Repair Technician Las Vegas"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">FAQ</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Pool Repair Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'How often should pool plaster be replaced?', a: 'Pool plaster typically lasts 10-15 years with proper maintenance. Signs you need replastering include rough texture, staining, visible cracks, or plaster flaking off.' },
              { q: 'How long does a pool repair take?', a: 'Minor repairs like tile replacement take 1-2 days. Complete resurfacing typically takes 5-7 days. We\'ll provide a detailed timeline during your free estimate.' },
              { q: 'Do you offer warranties on pool repairs?', a: 'Yes! We offer comprehensive warranties on all our work. Plaster resurfacing comes with a 5-year warranty, and equipment repairs include manufacturer warranties.' },
              { q: 'Can you repair my pool during winter?', a: 'Absolutely! Las Vegas weather allows for year-round pool repairs. In fact, winter is often the best time for major renovations since you\'re not using the pool.' },
            ].map((faq, index) => (
              <div key={index} className="bg-[#F8F6F3] rounded-xl p-6">
                <h3 className="text-lg font-bold text-[#2C2C2C] mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-red-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Restore Your Pool?</h2>
          <p className="text-white/90 text-lg mb-8">
            Get a free pool inspection and estimate from Las Vegas's trusted pool repair experts.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={openScheduler}
              className="inline-flex items-center gap-2 bg-white text-[#FF6B35] px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              Schedule Free Inspection
            </button>
            <a
              href="tel:7025001790"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call 702-500-1790 Today
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
